/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLock, 
  faEye, 
  faEyeSlash,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faArrowLeft,
  faShieldAlt,
  faKey,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import logoImage from '../assets/FINAL LOGO AK.png';
import '../styles/ResetPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        setError('Invalid or missing reset token');
        setIsValidatingToken(false);
        return;
      }

      try {
        // Properly encode the token and email for URL
        const encodedToken = encodeURIComponent(token);
        const encodedEmail = encodeURIComponent(email);
        const url = buildApiUrl(`/api/auth/validate-reset-token?token=${encodedToken}&email=${encodedEmail}`);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (response.ok) {
          setTokenValid(true);
          setMessage('Token validated. Please enter your new password.');
        } else {
          setError(data.error || data.message || 'Invalid or expired reset token');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setError('Failed to validate reset token');
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateToken();
  }, [token, email]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (error) setError('');
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) {
      errors.push(`At least ${minLength} characters`);
    }
    if (!hasUpperCase) {
      errors.push('One uppercase letter');
    }
    if (!hasLowerCase) {
      errors.push('One lowercase letter');
    }
    if (!hasNumbers) {
      errors.push('One number');
    }
    if (!hasSpecialChar) {
      errors.push('One special character');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setError(`Password must contain: ${passwordValidation.errors.join(', ')}`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(buildApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          email,
          password: formData.password,
          confirmPassword: formData.confirmPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        setMessage(data.message || 'Password updated successfully!');
        setError('');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Password updated successfully! Please log in with your new password.' 
            } 
          });
        }, 3000);
      } else {
        setError(data.error || data.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const securityFeatures = [
    {
      icon: faShieldAlt,
      title: "End-to-end Encryption"
    },
    {
      icon: faUserShield,
      title: "Secure Password Storage"
    },
    {
      icon: faKey,
      title: "Token-based Authentication"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const leftVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        delay: 0.2
      }
    }
  };

  const rightVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        delay: 0.4
      }
    }
  };

  if (isValidatingToken) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-layout">
          <div className="reset-password-right" style={{width: '100%'}}>
            <div className="reset-password-card">
              <div className="loading-container">
                <div className="loading-spinner">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </div>
                <p>Validating reset token...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-layout">
          <div className="reset-password-right" style={{width: '100%'}}>
            <div className="reset-password-card">
              <div className="error-container">
                <div className="error-icon">
                  <FontAwesomeIcon icon={faExclamationCircle} />
                </div>
                <h2>Invalid Reset Link</h2>
                <p>{error}</p>
                <button 
                  onClick={() => navigate('/forgot-password')}
                  className="btn btn-primary"
                >
                  Request New Reset Link
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <motion.div 
        className="reset-password-layout"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Left Side - Security Info */}
        <motion.div 
          className="reset-password-left"
          variants={leftVariants}
        >
          <div className="left-content">
            <div className="logo-section">
              <img src={logoImage} alt="AksharJobs Logo" className="main-logo" />
              <h1>AksharJobs</h1>
              <p className="tagline">CONNECT | DISCOVER | ELEVATE</p>
            </div>

            <div className="security-features-section">
              <h2>Your Security Matters</h2>
              <div className="security-features-grid">
                {securityFeatures.map((feature, index) => (
                  <motion.div 
                    key={index}
                    className="security-feature-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + (index * 0.1) }}
                  >
                    <div className="security-feature-icon">
                      <FontAwesomeIcon icon={feature.icon} />
                    </div>
                    <div className="security-feature-content">
                      <h3>{feature.title}</h3>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="password-policy-note">
              <div className="policy-icon">
                <FontAwesomeIcon icon={faLock} />
              </div>
              <div className="policy-text">
                <h3>Strong Password Policy</h3>
                <p>We enforce strong password requirements to keep your account safe and secure.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Side - Reset Form */}
        <motion.div 
          className="reset-password-right"
          variants={rightVariants}
        >
          <div className="reset-password-card">
            <div className="reset-password-header">
              <Link to="/login" className="back-link">
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to Login
              </Link>
              <h2>Set Your New Password</h2>
              <p>Your new password must be different from previously used passwords.</p>
              {email && (
                <div className="email-display">
                  Resetting password for: <strong>{email}</strong>
                </div>
              )}
            </div>

            <div className="reset-password-content">
              {!isSuccess ? (
                <form onSubmit={handleSubmit} className="reset-password-form">
                  
                  {/* New Password Input */}
                  <div className="form-group">
                    <div className="label-group">
                      <FontAwesomeIcon icon={faLock} className="form-label-icon" />
                      <label htmlFor="password">New Password</label>
                    </div>
                    <div className="password-input-container">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter your new password"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="password-toggle"
                        disabled={isLoading}
                        aria-label="Toggle password visibility"
                      >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    <div className="password-requirements">
                      <p>Password must contain:</p>
                      <ul>
                        <li className={formData.password.length >= 8 ? 'valid' : ''}>
                          At least 8 characters
                        </li>
                        <li className={/[A-Z]/.test(formData.password) ? 'valid' : ''}>
                          One uppercase letter
                        </li>
                        <li className={/[a-z]/.test(formData.password) ? 'valid' : ''}>
                          One lowercase letter
                        </li>
                        <li className={/\d/.test(formData.password) ? 'valid' : ''}>
                          One number
                        </li>
                        <li className={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'valid' : ''}>
                          One special character
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Confirm Password Input */}
                  <div className="form-group">
                    <div className="label-group">
                      <FontAwesomeIcon icon={faLock} className="form-label-icon" />
                      <label htmlFor="confirmPassword">Confirm New Password</label>
                    </div>
                    <div className="password-input-container">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Confirm your new password"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={toggleConfirmPasswordVisibility}
                        className="password-toggle"
                        disabled={isLoading}
                        aria-label="Toggle confirm password visibility"
                      >
                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    {formData.confirmPassword && (
                      <div className={`password-match ${formData.password === formData.confirmPassword ? 'valid' : 'invalid'}`}>
                        {formData.password === formData.confirmPassword ? (
                          <>
                            <FontAwesomeIcon icon={faCheckCircle} />
                            Passwords match
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            Passwords do not match
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Error Message */}
                  {error && (
                    <motion.div 
                      className="error-message"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FontAwesomeIcon icon={faExclamationCircle} />
                      {error}
                    </motion.div>
                  )}

                  {/* Success Message */}
                  {message && !isSuccess && (
                    <motion.div 
                      className="info-message"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {message}
                    </motion.div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="reset-password-button"
                    disabled={isLoading || !formData.password || !formData.confirmPassword}
                  >
                    {isLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Updating Password...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </form>
              ) : (
                <div className="success-content">
                  <div className="success-icon">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </div>
                  <h2>Password Updated Successfully!</h2>
                  <p className="success-message">{message}</p>
                  
                  <div className="success-details">
                    <p>Your password has been updated successfully. You will be redirected to the login page shortly.</p>
                  </div>

                  <div className="success-actions">
                    <button
                      onClick={() => navigate('/login')}
                      className="btn btn-primary"
                    >
                      Go to Login
                    </button>
                  </div>

                  <div className="countdown">
                    <p>Redirecting in <span>3</span> seconds...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
